import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { createEmailSchema, type EmailFormData } from '../../lib/validators';
import {
  useLocalizedSchema,
  useRevalidateOnLanguageChange,
} from '../../hooks/useLocalizedSchema';
import { forgotPasswordApi } from '../../services/api';
import { extractApiError } from '../../lib/errorCodes';
import { TextField, Button } from '../../components/ui/Form';
import './ForgotPasswordPage.css';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const emailSchema = useLocalizedSchema(createEmailSchema);

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  useRevalidateOnLanguageChange(trigger, isSubmitted);

  const onSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    try {
      await forgotPasswordApi(data.email);
      setEmailSent(true);
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="auth-forgot__done">
        <span className="auth-forgot__done-icon" aria-hidden="true">
          <Check size={26} strokeWidth={2.5} />
        </span>
        <h1 className="auth-forgot__title">{t('auth.forgotSentTitle')}</h1>
        <p className="auth-forgot__done-text">{t('general.passwordSent')}</p>
        <div className="auth-forgot__footer">
          <button type="button" className="auth-link" onClick={() => navigate('/')}>
            {t('general.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="auth-forgot__header">
        <h1 className="auth-forgot__title">{t('auth.forgotTitle')}</h1>
        <p className="auth-forgot__subtitle">{t('auth.forgotSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="auth-forgot__form" noValidate>
        <TextField
          label={t('general.emailLabel')}
          type="email"
          placeholder="votre.email@exemple.com"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Button type="submit" isLoading={isLoading} withArrow>
          {t('general.resetButton')}
        </Button>
      </form>

      <div className="auth-forgot__footer">
        <button type="button" className="auth-link" onClick={() => navigate('/')}>
          {t('general.backToLogin')}
        </button>
      </div>
    </>
  );
}
